import { IonContent, IonItem, IonLabel, IonList } from "@ionic/react";
import { ComponentProps, useState } from "react";
import { FormattedMessage } from "react-intl";

import RichTextareaFileInput from "@components/fields/RichTextareaFileInput";
import RichTextareaInAppLinksSelect from "@components/fields/RichTextareaInAppLinksSelect";
import RichTextareaMergeTagsSelect from "@components/fields/RichTextareaMergeTagsSelect";
import RichTextareaTemplatesSelect from "@components/fields/RichTextareaTemplatesSelect";
import Modal from "@components/Modal";
import IconBase from "@components/text/IconBase";
import useOpener from "@hooks/useOpener";
import { RichTextareaMenuControls } from "@utils/formUtils";
import { generateHTMLId } from "@utils/htmlUtils";

type Props = Omit<ComponentProps<typeof RichTextareaInAppLinksSelect>, "onSelect"> & {
  controls: RichTextareaMenuControls[];
  dismiss: () => void;
};

const RichTextareaExtraControlsPopover = ({ controls, dismiss, ...inAppLinksSelectProps }: Props) => {
  const [attachmentsInputId] = useState<string>(generateHTMLId());

  const inAppLinksModalOpener = useOpener();
  const mergeTagsModalOpener = useOpener();
  const templatesModalOpener = useOpener();

  const closeAll = () => {
    inAppLinksModalOpener.close();
    mergeTagsModalOpener.close();
    templatesModalOpener.close();
    dismiss();
  };

  return (
    <>
      <IonContent>
        <IonList className="ion-no-padding" color="dark">
          {controls.includes("templates") && (
            <IonItem button color="gray-500" lines="none" onClick={templatesModalOpener.open}>
              <IconBase className="ion-no-margin ion-margin-end" icon="rightToBracket" size="small" slot="start" />
              <IonLabel>
                <FormattedMessage id="forms.richTextArea.controls.templates" />
              </IonLabel>
            </IonItem>
          )}
          {controls.includes("inAppLinks") && (
            <IonItem button color="gray-500" lines="none" onClick={inAppLinksModalOpener.open}>
              <IconBase className="ion-no-margin ion-margin-end" icon="link" size="small" slot="start" />
              <IonLabel>
                <FormattedMessage id="forms.richTextArea.controls.inAppLinks" />
              </IonLabel>
            </IonItem>
          )}
          {controls.includes("attachments") && (
            // associating the label with the input means clicks on it will open the file picker
            <>
              <label htmlFor={attachmentsInputId}>
                <IonItem color="gray-500" lines="none">
                  <IconBase className="ion-no-margin ion-margin-end" icon="paperclip" size="small" slot="start" />
                  <IonLabel>
                    <FormattedMessage id="forms.richTextArea.controls.attachments" />
                  </IonLabel>
                </IonItem>
              </label>
              <RichTextareaFileInput id={attachmentsInputId} onChange={closeAll} />
            </>
          )}
          {controls.includes("mergeTags") && (
            <IonItem button color="gray-500" lines="none" onClick={mergeTagsModalOpener.open}>
              <IconBase className="ion-no-margin ion-margin-end" icon="at" size="small" slot="start" />
              <IonLabel>
                <FormattedMessage id="forms.richTextArea.controls.mergeTags" />
              </IonLabel>
            </IonItem>
          )}
        </IonList>
      </IonContent>
      {controls.includes("inAppLinks") && (
        <Modal backdropDismiss onIonModalDidDismiss={closeAll} opener={inAppLinksModalOpener}>
          {() => <RichTextareaInAppLinksSelect {...inAppLinksSelectProps} onSelect={closeAll} />}
        </Modal>
      )}
      {controls.includes("mergeTags") && (
        <Modal backdropDismiss onIonModalDidDismiss={closeAll} opener={mergeTagsModalOpener}>
          {() => <RichTextareaMergeTagsSelect onSelect={closeAll} />}
        </Modal>
      )}
      {controls.includes("templates") && (
        <Modal backdropDismiss onIonModalDidDismiss={closeAll} opener={templatesModalOpener}>
          {() => <RichTextareaTemplatesSelect onSelect={closeAll} />}
        </Modal>
      )}
    </>
  );
};

export default RichTextareaExtraControlsPopover;
